import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, DollarSign, Euro, ArrowRight, Bitcoin, Copy, ExternalLink, CheckCircle2, Clock, XCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** USD / EUR deposit via Stripe with live BRL quote. */
export function FxDepositDialog({ open, onOpenChange, currency }) {
  const [amount, setAmount] = useState(20);
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const meta = currency === "EUR" ? { label: "Euro", symbol: "€", Icon: Euro } : { label: "Dólar", symbol: "US$", Icon: DollarSign };

  useEffect(() => {
    if (!open || !amount || amount <= 0) return;
    const t = setTimeout(() => {
      axios.get(`${API}/wallet/fx/quote`, { params: { currency, amount } }).then((r) => setQuote(r.data)).catch(() => setQuote(null));
    }, 350);
    return () => clearTimeout(t);
  }, [open, amount, currency]);

  const pay = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/wallet/fx/checkout`, { currency, amount: parseFloat(amount), origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao iniciar pagamento"); setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid={`fx-deposit-dialog-${currency}`}>
        <DialogHeader><DialogTitle className="flex items-center gap-2"><meta.Icon className="w-5 h-5 text-primary" /> Depositar em {meta.label}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Label className="text-xs">Valor em {meta.label} ({meta.symbol} 2 a {meta.symbol} 2.000)</Label>
          <Input type="number" min={2} max={2000} step="1" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="fx-amount" />
          <div className="grid grid-cols-4 gap-2">
            {[10, 20, 50, 100].map((v) => <Button key={v} variant="outline" size="sm" className="rounded-full" onClick={() => setAmount(v)}>{meta.symbol} {v}</Button>)}
          </div>
          <div className="rounded-xl bg-muted p-3 text-sm" data-testid="fx-quote">
            {quote ? (
              <>
                <div className="flex items-center justify-between font-semibold"><span>{meta.symbol} {Number(amount).toFixed(2)}</span><ArrowRight className="w-4 h-4 text-muted-foreground" /><span className="text-emerald-600" data-testid="fx-brl-amount">R$ {quote.brl_amount.toFixed(2)}</span></div>
                <div className="text-[11px] text-muted-foreground mt-1">Cotação do dia: 1 {currency} = R$ {quote.rate.toFixed(4)}{quote.margin_pct > 0 ? ` · taxa ${quote.margin_pct}% → R$ ${quote.effective_rate.toFixed(4)}` : ""}</div>
              </>
            ) : <span className="text-muted-foreground flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Buscando cotação...</span>}
          </div>
          <p className="text-[11px] text-muted-foreground">Pagamento com cartão internacional via Stripe. O saldo é creditado em reais assim que o pagamento for confirmado.</p>
        </div>
        <DialogFooter>
          <Button onClick={pay} disabled={busy || !quote} className="w-full rounded-full h-11" data-testid="fx-pay-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <meta.Icon className="w-4 h-4 mr-1" />} Pagar {meta.symbol} {Number(amount || 0).toFixed(2)} com cartão
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const STATUS_META = {
  new: { label: "Aguardando escolha de rede", Icon: Clock, cls: "text-amber-600" },
  pending: { label: "Aguardando pagamento", Icon: Clock, cls: "text-amber-600" },
  confirming: { label: "Confirmando na blockchain", Icon: Loader2, cls: "text-sky-600" },
  paid: { label: "Pago — saldo creditado", Icon: CheckCircle2, cls: "text-emerald-600" },
  expired: { label: "Expirado", Icon: XCircle, cls: "text-red-600" },
  canceled: { label: "Cancelado", Icon: XCircle, cls: "text-red-600" },
  invalid: { label: "Inválido", Icon: XCircle, cls: "text-red-600" },
};

/** Crypto deposit via CoinGate: choose coin → hosted invoice / address + QR → poll status. */
export function CryptoDepositDialog({ open, onOpenChange, initialTx, onCredited }) {
  const [opts, setOpts] = useState(null);
  const [coin, setCoin] = useState("USDT");
  const [amount, setAmount] = useState(100);
  const [busy, setBusy] = useState(false);
  const [order, setOrder] = useState(null);

  useEffect(() => { if (open) axios.get(`${API}/wallet/crypto/options`).then((r) => setOpts(r.data)).catch(() => setOpts({ configured: false, currencies: [] })); }, [open]);
  useEffect(() => { if (open && initialTx) axios.get(`${API}/wallet/crypto/status/${initialTx}`).then((r) => setOrder(r.data)).catch(() => {}); }, [open, initialTx]);
  useEffect(() => {
    if (!open || !order || order.credited || ["paid", "expired", "canceled", "invalid"].includes(order.status)) return;
    const t = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/wallet/crypto/status/${order.tx_id}`);
        setOrder(r.data);
        if (r.data.credited) { toast.success("Depósito em cripto confirmado! Saldo creditado."); onCredited?.(); }
      } catch { /* noop */ }
    }, 6000);
    return () => clearInterval(t);
  }, [open, order, onCredited]);

  const create = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/wallet/crypto/order`, { amount_brl: parseFloat(amount), pay_currency: coin, origin_url: window.location.origin });
      setOrder(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao criar pedido"); }
    setBusy(false);
  };
  const copy = (v) => { navigator.clipboard?.writeText(v); toast.success("Copiado!"); };
  const st = order ? (STATUS_META[order.status] || STATUS_META.pending) : null;
  const qrData = order?.payment_request_uri || order?.payment_address;

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setOrder(null); }}>
      <DialogContent className="max-w-sm" data-testid="crypto-deposit-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Bitcoin className="w-5 h-5 text-amber-500" /> Depositar com criptomoedas</DialogTitle></DialogHeader>
        {opts && !opts.configured && !order ? (
          <div className="text-sm text-amber-700 bg-amber-500/10 rounded-lg p-3" data-testid="crypto-not-configured">Pagamentos em cripto ainda não estão ativos. O administrador precisa configurar a chave do CoinGate.</div>
        ) : !order ? (
          <div className="space-y-3">
            <Label className="text-xs">Valor a creditar (R$ 5 a R$ 50.000)</Label>
            <Input type="number" min={5} max={50000} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="crypto-amount" />
            <Label className="text-xs">Criptomoeda</Label>
            <div className="grid grid-cols-2 gap-2" data-testid="crypto-coin-list">
              {(opts?.currencies || []).map((c) => (
                <button key={c.code} type="button" onClick={() => setCoin(c.code)} data-testid={`crypto-coin-${c.code}`}
                  className={`text-left rounded-xl border px-3 py-2 text-sm transition-colors ${coin === c.code ? "border-primary bg-primary/5 font-semibold" : "border-border hover:bg-muted"}`}>
                  <span className="block">{c.code}</span><span className="block text-[10px] text-muted-foreground">{c.label}</span>
                </button>
              ))}
            </div>
            {opts?.mode === "sandbox" && <p className="text-[10px] text-amber-600">Modo sandbox (teste) — nenhuma cripto real é movimentada.</p>}
            <Button onClick={create} disabled={busy || !opts?.configured} className="w-full rounded-full h-11" data-testid="crypto-create-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Bitcoin className="w-4 h-4 mr-1" />} Gerar pagamento em {coin}
            </Button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="crypto-order">
            <div className={`flex items-center gap-2 text-sm font-semibold ${st.cls}`} data-testid="crypto-status"><st.Icon className={`w-4 h-4 ${order.status === "confirming" ? "animate-spin" : ""}`} /> {st.label}</div>
            <div className="text-sm">Crédito: <b>R$ {Number(order.amount_brl).toFixed(2)}</b>{order.pay_amount ? <> · Pagar <b>{order.pay_amount} {order.pay_currency}</b></> : null}</div>
            {qrData && (
              <div className="flex flex-col items-center gap-2">
                <img alt="QR Code" className="w-44 h-44 rounded-lg bg-white p-1 border" src={`https://api.qrserver.com/v1/create-qr-code/?size=176x176&data=${encodeURIComponent(qrData)}`} data-testid="crypto-qr" />
                {order.payment_address && (
                  <button onClick={() => copy(order.payment_address)} className="text-[11px] font-mono break-all text-left bg-muted rounded-lg p-2 hover:bg-muted/70 flex items-start gap-1" data-testid="crypto-address">
                    <Copy className="w-3 h-3 mt-0.5 shrink-0" /> {order.payment_address}
                  </button>
                )}
              </div>
            )}
            {order.payment_url && !order.credited && (
              <Button asChild variant={qrData ? "outline" : "default"} className="w-full rounded-full" data-testid="crypto-open-invoice">
                <a href={order.payment_url} target="_blank" rel="noreferrer"><ExternalLink className="w-4 h-4 mr-1" /> {qrData ? "Abrir fatura no CoinGate" : "Escolher rede e pagar no CoinGate"}</a>
              </Button>
            )}
            <p className="text-[11px] text-muted-foreground">O saldo é creditado automaticamente após a confirmação na blockchain. Você pode fechar esta janela — avisaremos por notificação.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
